import { BaseComponent } from '../../lib/base-component.js';
import { css, html } from '../../vendor/lit.min.js';

class PrivacyPolicy extends BaseComponent {
    static styles = css`
        :host { display: block; }
        h2 { font-size: 1.25rem; margin-top: 1.5rem; }
        h3 { font-size: 1.1rem; margin-top: 1rem; }
        ul { padding-left: 1.5rem; }
        table { width: 100%; margin: 1rem 0; }
        th, td { padding: 0.5rem; text-align: left; border-bottom: 1px solid #dee2e6; }
        th { font-weight: 600; }
    `;

    render() {
        return html`
            <p><strong>Last Updated: March 2026</strong></p>
            <p>This Privacy Policy describes how Dice Driven Design LLC ("Company," "we," "us," or "our") collects, uses, and shares information about you when you use the Dice Driven Design authentication service (the "Service").</p>
            <p>By using the Service, you agree to the collection and use of information in accordance with this Privacy Policy.</p>

            <h2>1. Information We Collect</h2>

            <h3>1.1 Information You Provide</h3>
            <p><strong>Account Information</strong>: Your account includes:</p>
            <ul>
                <li>Email address</li>
                <li>Username</li>
                <li>Password (stored as a secure hash)</li>
                <li>First and last name (optional)</li>
            </ul>

            <h3>1.2 Information Collected Automatically</h3>
            <p><strong>Usage Information</strong>: We automatically collect:</p>
            <ul>
                <li>Login timestamps</li>
                <li>IP address</li>
                <li>Browser type and device information</li>
            </ul>

            <h3>1.3 Information from Third Parties</h3>
            <p><strong>Single Sign-On</strong>: If you sign in with Microsoft Entra ID, we receive basic profile information (name, email) from your identity provider.</p>

            <h2>2. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
                <li><strong>Provide the Service</strong>: Authenticate your identity and manage your account</li>
                <li><strong>Ensure Security</strong>: Detect and prevent unauthorized access and fraud</li>
                <li><strong>Communicate with You</strong>: Send password reset emails and service-related notices</li>
                <li><strong>Comply with Legal Obligations</strong>: Meet legal requirements and respond to lawful requests</li>
            </ul>

            <h2>3. How We Share Your Information</h2>
            <p>We do not sell your personal information. We may share your information in the following circumstances:</p>

            <h3>3.1 With Dice Driven Design Applications</h3>
            <p>When you use the Service to log into other Dice Driven Design applications, those applications receive your basic profile information (user ID, email, username) via secure tokens.</p>

            <h3>3.2 With Service Providers</h3>
            <table>
                <thead>
                    <tr><th>Provider</th><th>Purpose</th><th>Data Shared</th></tr>
                </thead>
                <tbody>
                    <tr><td>Amazon Web Services (AWS)</td><td>Cloud hosting and infrastructure</td><td>All Service data</td></tr>
                    <tr><td>Amazon SES</td><td>Email delivery</td><td>Email addresses for password resets</td></tr>
                </tbody>
            </table>

            <h3>3.3 For Legal Reasons</h3>
            <p>We may disclose your information if required by law or to protect the rights, property, or safety of the Company, our users, or the public.</p>

            <h2>4. Data Retention</h2>
            <table>
                <thead>
                    <tr><th>Data Type</th><th>Retention Period</th></tr>
                </thead>
                <tbody>
                    <tr><td>Account Information</td><td>Duration of account + 30 days after deletion</td></tr>
                    <tr><td>Authentication Tokens</td><td>Until expiration or logout</td></tr>
                    <tr><td>Password Reset Tokens</td><td>1 hour (automatically expired)</td></tr>
                </tbody>
            </table>

            <h2>5. Your Rights and Choices</h2>
            <h3>5.1 Account Information</h3>
            <p>You can access and update your profile information through the Service.</p>

            <h3>5.2 Account Deletion</h3>
            <p>You may request deletion of your account by contacting us at privacy@dicedriven.com.</p>

            <h2>6. Security</h2>
            <p>We implement appropriate technical and organizational measures to protect your information, including:</p>
            <ul>
                <li>Passwords are stored using bcrypt hashing</li>
                <li>Authentication tokens are signed with RS256</li>
                <li>All connections use TLS encryption</li>
            </ul>
            <p>However, no method of transmission over the Internet or electronic storage is completely secure.</p>

            <h2>7. Children's Privacy</h2>
            <p>The Service is not directed to children under 13. We do not knowingly collect personal information from children under 13. If we learn that we have collected such information, we will delete it promptly. Contact us at privacy@dicedriven.com if you believe a child under 13 has provided us with personal information.</p>

            <h2>8. International Data Transfers</h2>
            <p>The Service is operated from the United States. If you are located outside the United States, your information will be transferred to and processed in the United States. By using the Service, you consent to this transfer.</p>

            <h2>9. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. If we make material changes, we will notify you by posting a notice on the Service. Your continued use constitutes acceptance of the updated policy.</p>

            <h2>10. Contact Us</h2>
            <p>If you have questions about this Privacy Policy, please contact us at:</p>
            <p><strong>Dice Driven Design LLC</strong><br/>Email: privacy@dicedriven.com</p>
        `;
    }
}

customElements.define('privacy-policy', PrivacyPolicy);
